<script setup lang="ts">
import { computed } from 'vue'
import { formatDateTimeToLocaleString } from '@/utils/dateTimeHelpers'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import {
  NXGDSCondensedIndicator,
  NXGDSCondensedIndicatorVariants,
  NXGDSStatusTag,
  NXGDSStatusTagVariants,
} from '@nsftx/seven-nxg-design-system'
import { useVoucherStore, VoucherStatus } from '@nsftx/seven-client-retail-vue-sgp'

const MS_IN_MINUTE = 60_000
const { t } = useI18n()
const { voucher } = storeToRefs(useVoucherStore())

const formatRemainingValidity = (validUntil: string): string => {
  const expiresAt = new Date(validUntil).getTime()

  if (Number.isNaN(expiresAt)) {
    return '-'
  }

  const diffMs = Math.max(0, expiresAt - Date.now())
  const totalMinutes = Math.floor(diffMs / MS_IN_MINUTE)
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60

  return `${days}d ${hours}h ${minutes}min`
}

const headerTagData = computed(() => {
  if (voucher.value?.status.resolutionStatus === VoucherStatus.EXPIRED) {
    return {
      label: t('voucher_details_header_status_expired'),
      variant: NXGDSStatusTagVariants.negative,
    }
  } else if (voucher.value?.status.resolutionStatus === VoucherStatus.REDEEMED) {
    return {
      label: t('voucher_details_header_status_redeemed'),
      variant: NXGDSStatusTagVariants.positive,
    }
  } else if (voucher.value?.status.resolutionStatus === VoucherStatus.REVOKED) {
    return {
      label: t('voucher_details_header_status_revoked'),
      variant: NXGDSStatusTagVariants.subtle,
    }
  }

  return {
    label: t('voucher_details_header_status_valid', {
      date: formatRemainingValidity(voucher.value?.context.validUntil ?? ''),
    }),
    variant: NXGDSStatusTagVariants.attention,
  }
})
</script>

<template>
  <div class="ss-voucher-details-modal__header">
    <div class="ss-voucher-details-modal__header-title">
      <h4>{{ t('common_voucher') }}</h4>
      <NXGDSStatusTag :label="headerTagData.label" :variant="headerTagData.variant" />
    </div>
    <div class="ss-voucher-details-modal__header-subtitle">
      {{ formatDateTimeToLocaleString(voucher?.context.issuedAt ?? '') }}
      <NXGDSCondensedIndicator :variant="NXGDSCondensedIndicatorVariants.universal" />
      {{ t('deposits_vouchers_table_header_voucher_id') }}: {{ voucher?.barcode }}
    </div>
  </div>
</template>

<style lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.ss-voucher-details-modal {
  &__header {
    display: flex;
    justify-content: space-between;
    gap: var(--nxgds-alias-spacing-xs);
    flex-direction: column;

    &-title {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      @include typography('h4', 'regular');
    }

    &-subtitle {
      display: flex;
      align-items: center;
      gap: var(--nxgds-alias-spacing-xs);
      color: var(--nxgds-mapped-color-text-secondary);
      @include typography('label', 'regular');
    }
  }
}
</style>
